/* ============================================================
   PersonCell — person 속성 조직 멤버 선택기
   - 셀 클릭 → 멤버 검색 팝오버 → 다중 선택/해제
   - 저장 포맷(canonical): user id 문자열 배열 ['<uuid>', ...]
   - 하위호환: 레거시 평문 문자열(이름)은 그대로 칩으로 표시.
     편집 시작 시 이름이 일치하는 멤버가 있으면 선택 상태로 매칭.
   ============================================================ */
import { useEffect, useMemo, useRef, useState } from 'react';
import { avaColor } from './viewPipeline';
import { personNames } from './computeValue';

function useOutsideClose(ref, onClose) {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose]);
}

/* 아바타(이니셜+색) 칩 목록 — 뷰 카드에서도 재사용 가능 */
export function PersonChips({ value, peopleById }) {
  const names = personNames(value, peopleById);
  if (!names.length) return null;
  return (
    <span className="ps-chips">
      {names.map((n, i) => (
        <span className="person" key={`${n}_${i}`}>
          <span className="mav" style={{ background: avaColor(n) }}>{n[0]}</span>
          <span className="ps-name">{n}</span>
        </span>
      ))}
    </span>
  );
}

export default function PersonCell({ col, value, onChange, members, peopleById }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useOutsideClose(ref, () => setOpen(false));

  // 선택된 user id 집합
  const selected = useMemo(() => {
    if (Array.isArray(value)) return new Set(value.filter((v) => typeof v === 'string'));
    if (typeof value === 'string' && value) {
      // 레거시 평문 — 이름이 일치하는 멤버를 선택 상태로 간주
      const hit = (members || [])
        .filter((m) => (m.display_name || '') === value)
        .map((m) => m.user_id)
        .filter(Boolean);
      return new Set(hit);
    }
    return new Set();
  }, [value, members]);

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (members || []).filter((m) => !s || (m.display_name || '').toLowerCase().includes(s));
  }, [members, q]);

  const toggle = (uid) => {
    if (!uid) return;
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid);
    else next.add(uid);
    onChange(col.key, [...next]); // 저장: user id 배열
  };

  const names = personNames(value, peopleById);

  return (
    <div className="cell ps-cell" ref={ref}>
      <span
        className="ps-trigger"
        onClick={() => setOpen((o) => !o)}
        role="button"
        title="담당자 선택"
      >
        {names.length ? (
          <PersonChips value={value} peopleById={peopleById} />
        ) : (
          <span className="tag-trigger muted">비어 있음</span>
        )}
      </span>
      {open && (
        <div className="opt-pop ps-pop">
          <div className="ps-search">
            <input
              autoFocus
              value={q}
              placeholder="멤버 검색…"
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
            />
          </div>
          <div className="opt-list">
            {list.length === 0 && <div className="ps-empty">멤버가 없습니다.</div>}
            {list.map((m) => {
              const name = m.display_name || '(이름 없음)';
              const on = selected.has(m.user_id);
              return (
                <div
                  key={m.id}
                  className={`opt-item ps-item${on ? ' on' : ''}`}
                  onClick={() => toggle(m.user_id)}
                >
                  <span className="mav" style={{ background: avaColor(name) }}>{name[0]}</span>
                  <span className="ps-name">{name}</span>
                  {on && <span className="ps-check">✓</span>}
                </div>
              );
            })}
          </div>
          {selected.size > 0 && (
            <button className="ps-clear" onClick={() => onChange(col.key, [])}>모두 지우기</button>
          )}
        </div>
      )}
    </div>
  );
}
